/**
 * Results — Eva sees her first-pass attempts laid out next to the correct
 * spellings, with an encouraging-not-punishing marker per word.
 * Implements spec §4.3.
 *
 * Spec constraints honoured here:
 *   - **No score number on this screen.** ("The point is for her to see
 *     the work itself, not collapse it to a number prematurely.") The
 *     numeric score belongs on the celebration screen (Phase 6).
 *   - **No red X.** Incorrect attempts get a soft warm wash (the same
 *     `--wash` token used for second chance, §4.4) over the typed text.
 *     Correct attempts get a soft star in the warm-green `--correct`.
 *   - **The attempt itself is shown**, not just a tick/cross — that's
 *     the whole point of the screen, surfacing her own work for review.
 *
 * After Continue:
 *   - If she missed at least one word → second-chance intro → second chance.
 *   - If all 10 correct → celebration. A perfect session skips
 *     second-chance and 3x-write entirely (spec §2).
 *
 * The intermediate intro screen (PhaseIntroScreen "secondChance")
 * gives Eva a Pumpkin-led "here's what's next" beat before the retry
 * input so the handoff doesn't feel abrupt.
 */
import { useEffect, useMemo } from "react";

import { Button } from "@/components/Button";
import { useNavigation } from "@/context/NavigationContext";
import { useSession } from "@/context/SessionContext";
import { playTransition } from "@/lib/sounds";
import { attemptMatches } from "@/lib/spellingCompare";
import "./ResultsScreen.css";

interface RowResult {
  wordId: number;
  spelling: string;
  attempt: string;
  matched: boolean;
}

export function ResultsScreen() {
  const { navigate } = useNavigation();
  const { session } = useSession();

  useEffect(() => {
    if (session === null || session.words.length === 0) navigate("home");
  }, [session, navigate]);

  // Paper-rustle on arrival — the "results page" turn (design.md §8).
  // Fires once on mount, regardless of whether her session has misses;
  // the wash fade-in (CSS) handles the visual side of any wrong rows.
  useEffect(() => {
    void playTransition();
  }, []);

  const rows: RowResult[] = useMemo(() => {
    if (!session) return [];
    return session.words.map((w) => {
      const attempt = session.firstPassAttempts[w.id] ?? "";
      return {
        wordId: w.id,
        spelling: w.spelling,
        attempt,
        matched: attemptMatches(attempt, w.spelling),
      };
    });
  }, [session]);

  if (!session) return null;

  const allCorrect = rows.every((r) => r.matched);

  const handleContinue = () => {
    if (allCorrect) navigate("celebration");
    else navigate("phaseIntro:secondChance");
  };

  return (
    <main className="results">
      <h1 className="results__title">Here&rsquo;s what you wrote</h1>

      <ul className="results__list">
        {rows.map((r) => (
          <li
            key={r.wordId}
            className={`results__row ${r.matched ? "is-correct" : "is-incorrect"}`}
          >
            <span
              className="results__marker"
              aria-label={r.matched ? "Correct" : "Try again"}
            >
              {r.matched ? "★" : ""}
            </span>
            <div className="results__attempt-block">
              <span
                className={`results__attempt ${r.matched ? "" : "results__attempt--wash"}`}
              >
                {r.attempt === "" ? (
                  <em className="results__empty">(nothing yet)</em>
                ) : (
                  r.attempt
                )}
              </span>
              {!r.matched && (
                <span className="results__correct">
                  <span className="results__correct-label">should be</span>{" "}
                  <span className="results__correct-word">{r.spelling}</span>
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>

      <div className="results__continue">
        <Button size="large" onClick={handleContinue}>
          {allCorrect ? "See your celebration" : "Let's try again"}
        </Button>
      </div>
    </main>
  );
}
